import { useMemo, useRef } from 'react';

import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

import { CursorPosition, useCursorPosition } from './Cursor';

export function translateCursorPositionToDataIndex<Data>({
  cursor,
  data,
  getTimestamp,
}: {
  cursor: CursorPosition;
  data?: Data[];
  getTimestamp: (data: Data) => Date | Timestamp['output'] | undefined;
}) {
  const cursorDate = cursor?.date;
  if (!cursorDate || !data) return null;
  const foundIndex = data.findIndex((bucket, i, arr) => {
    const timestamp = getTimestamp(bucket);
    if (!timestamp) return false;
    const nextbucket = arr[i + 1];
    const nextTimestamp = nextbucket && getTimestamp(nextbucket);
    const diff = Math.abs(cursorDate.diff(timestamp));
    const nextDiff = nextTimestamp && Math.abs(cursorDate.diff(nextTimestamp));
    cursorDate.diff(timestamp);
    return typeof nextDiff !== 'number' || nextDiff > diff;
  });
  return foundIndex === -1 ? null : foundIndex;
}

/**
 * Custom hook to get the index of the series data that the cursor is currently over.
 *
 * @param data - The time series data.
 * @param getTimestamp - Function to extract the timestamp from a data record.
 *
 * @returns The index of the data record the cursor is currently over.
 *
 * @example
 *
 * const cursorValueIndex = useCursorDateIndex({
 *   data: series,
 *   getTimestamp: (bucket) => bucket.timestamp,
 * });
 */
export function useCursorDateIndex<Data>({
  data,
  getTimestamp,
}: {
  data?: Data[];
  getTimestamp: (data: Data) => Date | Timestamp['output'] | undefined;
}) {
  const getTimestampRef = useRef(getTimestamp);
  const cursor = useCursorPosition();
  return useMemo(() => {
    return translateCursorPositionToDataIndex({
      cursor,
      data,
      getTimestamp: getTimestampRef.current,
    });
  }, [cursor, data]);
}
